import { Container, Graphics, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import { WEAPONS } from '@/config/weapons';
import { weaponCrateKey, weaponGlyphKey } from '@/config/weapon-visuals';
import { drawCardIcon } from './card-icons';
import {
  crossGlyph, deltaGlyph, padlockGlyph, plusGlyph, sparkleGlyph, swapGlyph,
} from './hangar-icons';
import { BAG_C, CRATE_C, MAIN_H, SOCKET_C } from './shop-compact-layout';
import { scrapBadge, type BagTileView, type SocketTileView } from './shop-hangar-tiles';
import { scrapGems } from './weapon-labels';
import { chamferPanel, hexPath, label, targetBrackets } from './widgets';

/*
 * Phone HANGAR tiles (polish 1): the wave-4 socket / bag / crate language
 * at phone size — numbered sockets, ▲/▼ bag tiles, the padlocks and the
 * swap-or-scrap crate. Numbers ≥ 15 px, every tap target ≥ 48 design px.
 */

const INK = 0x0f0c1a;
const DIM = 0x4a4256;
/** Scrap ✕ badge on a bag tile: drawn small, tapped big */
const BADGE_AT = { x: 15, y: 15 } as const;
const BADGE_HIT_R = 24;
const NUM_SIZE = 17;

function gemPrice(c: Container, cx: number, cy: number, price: number, red: boolean, on: boolean): void {
  const gem = new Sprite(getTexture(red ? 'pickup/gem-red' : 'pickup/gem-yellow'));
  gem.anchor.set(1, 0.5);
  gem.scale.set(0.5);
  gem.position.set(cx - 2, cy);
  const n = label(String(price), NUM_SIZE, red ? COLORS.gemRed : COLORS.gemYellow, 1);
  n.anchor.set(0, 0.5);
  n.position.set(cx + 2, cy);
  gem.alpha = n.alpha = on ? 1 : 0.45;
  c.addChild(gem, n);
}

function brackets(c: Container, w: number, h: number, sel: boolean, target: boolean): void {
  if (!sel && !target) return;
  const g = new Graphics();
  if (target) targetBrackets(g, w, h, COLORS.playerYellow, 12, sel ? 9 : 4);
  if (sel) targetBrackets(g, w, h, COLORS.uiAccent, 14, 4);
  c.addChild(g);
}

/** Numbered socket 1-2-3: weapon glyph + live power / empty + / padlock + price. */
export function compactSocket(v: SocketTileView): Container {
  const { w, h } = SOCKET_C;
  const c = new Container();
  const def = v.weaponId ? WEAPONS[v.weaponId] : undefined;
  const cat = def ? WEAPON_CATEGORY_COLOR[def.category] : DIM;
  const g = new Graphics();
  chamferPanel(g, w, h, {
    fill: def ? 0x15111f : v.open ? 0x110e1b : 0x0d0a16,
    stroke: def ? cat : v.target ? COLORS.playerYellow : v.price !== null ? 0x5a2a36 : 0x2a2440,
    strokeWidth: def ? 3 : 2, notch: 12,
  });
  hexPath(g, 18, 18, 14).fill(v.target ? COLORS.playerYellow : def ? cat : v.open ? DIM : 0x2a2440);
  c.addChild(g);
  const num = label(String(v.index + 1), 16, v.open || v.target ? INK : 0x5a6168, 0);
  num.anchor.set(0.5);
  num.position.set(18, 19);
  c.addChild(num);
  const m = new Graphics();
  c.addChild(m);
  if (def) {
    const glyph = new Sprite(getTexture(weaponGlyphKey(def.id)));
    glyph.anchor.set(0.5);
    glyph.scale.set(0.72);
    glyph.position.set(w / 2, 56);
    c.addChild(glyph);
    drawCardIcon(m, 'damage', COLORS.statPower).scale.set(0.36);
    m.position.set(24, 100);
    const p = label(String(Math.round(v.power)), NUM_SIZE, 0xffffff, 1);
    p.anchor.set(0, 0.5);
    p.position.set(36, 100);
    c.addChild(p);
    if (v.fresh) c.addChild(sparkleGlyph(new Graphics(), w - 14, 16, 8, COLORS.playerYellow));
  } else if (v.open) {
    plusGlyph(m, w / 2, 64, 16, v.target ? COLORS.playerYellow : DIM);
  } else if (v.price !== null) {
    padlockGlyph(m, w / 2, 52, 16, v.affordable ? COLORS.playerCream : 0x5a6168);
    gemPrice(c, w / 2, 96, v.price, true, v.affordable);
  } else {
    padlockGlyph(m, w / 2, 62, 12, 0x3a3548);
  }
  brackets(c, w, h, v.selected, v.target);
  return c;
}

/** Bag weapon: glyph, ▲/▼ power change vs its target socket, ✕ scrap badge. */
export function compactBagTile(v: BagTileView, onScrap: () => void): Container {
  const { w, h } = BAG_C;
  const c = new Container();
  const def = WEAPONS[v.weaponId]!;
  const g = new Graphics();
  chamferPanel(g, w, h, {
    fill: v.armed ? 0x2a1018 : 0x15111f,
    stroke: v.armed ? COLORS.dangerRed : WEAPON_CATEGORY_COLOR[def.category],
    strokeWidth: 3, notch: 10, plain: true,
  });
  c.addChild(g);
  const glyph = new Sprite(getTexture(weaponGlyphKey(def.id)));
  glyph.anchor.set(0.5);
  glyph.scale.set(0.6);
  glyph.position.set(w / 2, 40);
  if (v.armed) glyph.alpha = 0.4;
  c.addChild(glyph);
  if (v.fresh) c.addChild(sparkleGlyph(new Graphics(), w - 12, 13, 7, COLORS.playerYellow));
  const flat = Math.abs(v.delta) < 0.5;
  const color = flat ? 0x8a9099 : v.delta > 0 ? COLORS.statUp : COLORS.statDown;
  const d = new Graphics();
  if (!flat) deltaGlyph(d, w / 2 - 20, 76, 6, color, v.delta > 0);
  c.addChild(d);
  const txt = label(flat ? '=' : `${v.delta > 0 ? '+' : ''}${Math.round(v.delta)}`, 15, color, 1);
  txt.anchor.set(0, 0.5);
  txt.position.set(w / 2 - 10, 76);
  c.addChild(txt);
  if (v.showScrap || v.armed) scrapBadge(c, v.weaponId, v.armed, onScrap, BADGE_AT, BADGE_HIT_R);
  brackets(c, w, h, v.selected, false);
  return c;
}

export function compactEmptyBag(): Container {
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, BAG_C.w, BAG_C.h, {
    fill: 0x110e1b, fillAlpha: 0.6, stroke: 0x2a2440, strokeWidth: 2, notch: 10, plain: true,
  });
  c.addChild(g);
  return c;
}

/** Bag padlock: one more slot for yellow. */
export function compactBagBuy(price: number, affordable: boolean, selected: boolean): Container {
  const { w, h } = BAG_C;
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, h, { fill: 0x0d0a16, stroke: 0x4a3c1e, strokeWidth: 2, notch: 10, plain: true });
  padlockGlyph(g, w / 2, 36, 15, affordable ? COLORS.playerCream : 0x5a6168);
  c.addChild(g);
  gemPrice(c, w / 2, 72, price, false, affordable);
  brackets(c, w, h, selected, false);
  return c;
}

/** Tap button inside the crate tile (stops the tile's own tap). */
function crateButton(
  c: Container, y: number, h: number, primary: boolean, selected: boolean,
  draw: (b: Container, w: number) => void, act: () => void,
): void {
  const w = CRATE_C.w - 8;
  const b = new Container();
  const bg = new Graphics();
  chamferPanel(bg, w, h, {
    fill: primary ? (selected ? 0x1d4a44 : 0x16332f) : 0x24121a,
    stroke: primary ? (selected ? COLORS.uiAccent : 0x2e8c80) : COLORS.dangerRed,
    strokeWidth: primary && selected ? 3 : 2, notch: 8, plain: true,
  });
  b.addChild(bg);
  draw(b, w);
  b.position.set(4, y);
  b.eventMode = 'static';
  b.cursor = 'pointer';
  b.on('pointertap', (e) => {
    e.stopPropagation();
    act();
  });
  c.addChild(b);
}

/** Swap-or-scrap crate (full bag): ⇄ into socket n, or ✕ for +N gems. */
export function compactCrate(
  weaponId: string, target: number, selected: boolean, onSwap: () => void, onScrap: () => void,
): Container {
  const w = CRATE_C.w;
  const h = MAIN_H;
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, h, { fill: 0x1c120f, stroke: COLORS.dangerOrange, strokeWidth: 3, notch: 10 });
  c.addChild(g);
  const crate = new Sprite(getTexture(weaponCrateKey(weaponId)));
  crate.anchor.set(0.5);
  crate.scale.set(0.72);
  crate.position.set(w / 2, 40);
  c.addChild(crate, sparkleGlyph(new Graphics(), w - 10, 12, 7, COLORS.playerYellow));
  crateButton(c, 80, 64, true, selected, (b, bw) => {
    const m = swapGlyph(new Graphics(), bw / 2, 20, 10, 0xffffff);
    hexPath(m, bw / 2, 46, 12).fill(COLORS.playerYellow);
    const n = label(String(target + 1), 15, INK, 0);
    n.anchor.set(0.5);
    n.position.set(bw / 2, 47);
    b.addChild(m, n);
  }, onSwap);
  crateButton(c, 152, 68, false, selected, (b, bw) => {
    const m = crossGlyph(new Graphics(), bw / 2, 18, 7, COLORS.dangerRed);
    const gem = new Sprite(getTexture('pickup/gem-yellow'));
    gem.anchor.set(0.5);
    gem.scale.set(0.42);
    gem.position.set(12, 48);
    const v = label(`+${scrapGems(weaponId)}`, 14, COLORS.gemYellow, 0);
    v.anchor.set(0, 0.5);
    v.position.set(22, 48);
    b.addChild(m, gem, v);
  }, onScrap);
  if (selected) c.addChild(targetBrackets(new Graphics(), w, h, COLORS.uiAccent, 12, 4));
  return c;
}
